import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      danger: string
    }
    dashboard: {
      primary: string
      primaryLight: string
      success: string
      successLight: string
      info: string
      infoLight: string
      warning: string
      warningLight: string
    }
  }

  interface ThemeOptions {
    status?: {
      danger?: string
    }
    dashboard?: {
      primary?: string
      primaryLight?: string
      success?: string
      successLight?: string
      info?: string
      infoLight?: string
      warning?: string
      warningLight?: string
    }
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#E96D17', // Orange from your specification
      light: '#FF9F4A',
      dark: '#B54400',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#20069D', // Blue from your specification  
      light: '#5A41CC',
      dark: '#0D0070',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#20069D', // Blue from your specification
      light: '#5A41CC',
      dark: '#0D0070',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#37B256', // Green from your specification
      light: '#6FD47F',
      dark: '#228B22',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#E96D17', // Orange from your specification
      light: '#FF9F4A',
      dark: '#B54400',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#E93640', // Red from your specification
      light: '#FF6B73',
      dark: '#B50E1A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#263238',
      secondary: '#546E7A',
    },
    grey: {
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#B9B9B9', // Light grey from your specification
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#333333',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#333333',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#333333',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#333333',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#333333',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#333333',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#666666',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#666666',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      color: '#333333',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      color: '#666666',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(233, 109, 23, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(233, 109, 23, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(185,185,185,0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundImage: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundImage: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)',
        },
        elevation1: {
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: 'rgba(185,185,185,0.2)',
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '1rem',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(233, 109, 23, 0.08)',
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(233, 109, 23, 0.12)',
            borderLeft: '4px solid #E96D17',
            '&:hover': {
              backgroundColor: 'rgba(233, 109, 23, 0.16)',
            },
          },
        },
      },
    },
  },
  status: {
    danger: '#E93640', // Red from your specification
  },
  dashboard: {
    primary: '#E96D17', // Orange from your specification
    primaryLight: '#FF9F4A',
    success: '#37B256', // Green from your specification
    successLight: '#6FD47F',
    info: '#20069D', // Blue from your specification
    infoLight: '#5A41CC',
    warning: '#E96D17', // Orange from your specification
    warningLight: '#FF9F4A',
  },
})

export default theme
